// src/pages/workflow/PromptTemplateListPage.tsx
// Manage AI prompt templates (prompts as data, not code).

import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Badge,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { getEntityId, formatRelativeTime } from '@/features/workflow/logic';
import type { PromptTemplate } from '@core/types/workflow/PromptTemplate';

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export const PromptTemplateListPage = observer(() => {
  const store = useWorkflow();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const res = await store.ListPromptTemplates();
      setTemplates(res?.prompt_templates?.items || []);
    } catch {
      // Error handled by store
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let items = templates;
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (tpl) =>
          (tpl.name || '').toLowerCase().includes(q) ||
          (tpl.slug || '').toLowerCase().includes(q) ||
          (tpl.description || '').toLowerCase().includes(q),
      );
    }
    if (activeFilter === 'active') items = items.filter((tpl) => tpl.is_active);
    if (activeFilter === 'inactive') items = items.filter((tpl) => !tpl.is_active);
    return items;
  }, [templates, search, activeFilter]);

  const handleDelete = async (tpl: PromptTemplate) => {
    if (tpl.is_builtin) return;
    if (!confirm(t('workflow.deletePromptTemplateConfirm'))) return;
    try {
      await store.DeletePromptTemplate(getEntityId(tpl));
      loadTemplates();
    } catch {
      // Error handled by store
    }
  };

  const handleClone = (tpl: PromptTemplate) => {
    navigate('/workflows/prompt-templates/new', { state: { clone: tpl } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm">
            <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('workflow.promptTemplates')}</span>
          </nav>
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('workflow.promptTemplates')}</h1>
        </div>
        <Button size="sm" onClick={() => navigate('/workflows/prompt-templates/new')}>
          <PlusIcon className="h-4 w-4 mr-1.5" />
          {t('workflow.addPromptTemplate')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Input
          placeholder={t('workflow.searchPromptTemplates')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
        />
        <Select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
          <option value="all">{t('common.all')}</option>
          <option value="active">{t('workflow.filterActive')}</option>
          <option value="inactive">{t('workflow.filterInactive')}</option>
        </Select>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {isLoading ? (
        <Card>
          <CardContent className="p-5">
            <div className="animate-pulse space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 bg-muted rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <svg className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 8h10M7 12h6m-9 8V6a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H8l-4 3z" />
            </svg>
            <h3 className="text-lg font-medium text-foreground mb-1">{t('workflow.noPromptTemplates')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('workflow.noPromptTemplatesDesc')}</p>
            <Button onClick={() => navigate('/workflows/prompt-templates/new')}>{t('workflow.addPromptTemplate')}</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('workflow.field.name')}</TableHead>
                <TableHead>{t('workflow.field.slug')}</TableHead>
                <TableHead>{t('workflow.field.model')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((tpl) => {
                const id = getEntityId(tpl);
                return (
                  <TableRow
                    key={id || tpl.slug}
                    className="cursor-pointer"
                    onClick={() => navigate(`/workflows/prompt-templates/${id}`)}
                  >
                    <TableCell>
                      <div className="font-medium text-foreground">{tpl.name}</div>
                      {tpl.description && (
                        <div className="text-xs text-muted-foreground line-clamp-1">{tpl.description}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-mono text-muted-foreground">{tpl.slug}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground font-mono">{tpl.model || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {tpl.is_active ? (
                          <Badge variant="success" size="sm">{t('workflow.filterActive')}</Badge>
                        ) : (
                          <Badge variant="secondary" size="sm">{t('workflow.filterInactive')}</Badge>
                        )}
                        {tpl.is_builtin && <Badge variant="secondary" size="sm">{t('workflow.builtin')}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <span className="text-xs text-muted-foreground mr-2 hidden sm:inline">
                          {formatRelativeTime(tpl.updated_at || tpl.created_at)}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => handleClone(tpl)}>
                          {t('workflow.clone')}
                        </Button>
                        <span title={tpl.is_builtin ? t('workflow.builtinReadOnly') : undefined}>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={tpl.is_builtin}
                            onClick={() => navigate(`/workflows/prompt-templates/${id}`)}
                          >
                            {t('common.edit')}
                          </Button>
                        </span>
                        <span title={tpl.is_builtin ? t('workflow.builtinReadOnly') : undefined}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            disabled={tpl.is_builtin}
                            onClick={() => handleDelete(tpl)}
                          >
                            {t('common.delete')}
                          </Button>
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
});

export default PromptTemplateListPage;
